import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: { path: '/dashboard', pageLabel: '대시보드' },
  daily: {
    path: '/dashboard',
    openSelector: '[data-tutorial-dash="view-daily"]',
    pageLabel: '대시보드',
    openLabel: '데일리',
  },
  weekly: {
    path: '/dashboard',
    openSelector: '[data-tutorial-dash="view-weekly"]',
    pageLabel: '대시보드',
    openLabel: '위클리',
  },
  calendar: {
    path: '/dashboard',
    openSelector: '[data-tutorial-dash="open-calendar"]',
    pageLabel: '대시보드',
    openLabel: '달력',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-dashboard': R.page,
  'view-toggle': R.page,
  'daily-lessons': R.daily,
  'daily-date-nav': R.daily,
  'daily-pick-slot': R.daily,
  'daily-records': R.daily,
  'daily-todo': R.daily,
  'daily-todo-add': R.daily,
  'switch-weekly': R.page,
  'weekly-overview': R.weekly,
  'weekly-toolbar': R.weekly,
  'open-calendar': R.weekly,
  'calendar-modal': R.calendar,
  'weekly-lessons': R.weekly,
  'weekly-lesson-menu': R.weekly,
  'weekly-todos': R.weekly,
};

const dashboardNoteRaw: TutorialDefinition = {
  id: 'dashboard-note',
  title: '대시보드',
  summary: '데일리·위클리로 오늘 수업과 주간 일정을 보고, 기록·TODO까지 다루는 방법을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '대시보드 튜토리얼을 시작해요! 데일리로 하루 수업·TODO를 보고, 위클리로 한 주를 한눈에 보는 방법까지 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '대시보드를 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-dashboard',
      mascot:
        '사이드바 맨 위 로고(또는 상단 로고)를 눌러 대시보드로 이동해 주세요. 단축키 Shift+H로도 이동할 수 있어요.',
      targetSelector:
        'a[href^="/dashboard"].cp-sidebar-app-title-link, a.cp-logo[href^="/dashboard"], a[href="/dashboard"]',
      placement: 'right',
      advanceShortcut: { key: 'h', shift: true },
      advanceOnPathPrefix: '/dashboard',
      section: '[대시보드] 시작',
      sectionMascot: '대시보드 페이지로 이동하는 구간이에요.',
    },
    {
      id: 'view-toggle',
      mascot:
        '왼쪽 위 "데일리"·"위클리"를 눌러 보기 방식이 바뀌는 걸 확인해 보세요. 이어서 진행하려면 다시 "데일리"로 맞춘 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-dashboard-page__view-bar .cp-todo-note-view-toggle, [data-tutorial-dash="view-toggle"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '[data-tutorial-dash="view-daily"].is-active, [data-tutorial-dash="view-daily"][aria-selected="true"]',
      section: '[대시보드] 데일리',
      sectionMascot: '하루 단위로 수업과 TODO를 보는 구간이에요.',
    },
    {
      id: 'daily-lessons',
      mascot:
        '왼쪽 "오늘 수업" 섹션에는 선택한 날짜의 교시·편제·단원·학습내용이 블럭으로 보여요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="daily-lessons"], .cp-dashboard-sections--with-todo > .cp-dashboard-block:first-child',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'daily-date-nav',
      mascot:
        '날짜 옆 "<"·">"로 하루씩 이동하고, 가운데 날짜 제목을 누르면 오늘로 돌아가요. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="daily-date-nav"], .cp-dashboard-lesson-head-left',
      manualAdvance: true,
    },
    {
      id: 'daily-pick-slot',
      mascot:
        '수업 블럭을 누르면 위클리처럼 미니 메뉴가 열려요. 한 번 눌러 본 뒤 "다음"을 눌러 주세요. 수업이 없으면 "다음"으로 넘어가도 돼요.',
      targetSelector:
        '[data-tutorial-dash="daily-slot"], .cp-dashboard-slot--selectable',
      manualAdvance: true,
      targetMissingHint:
        '표시할 수업 편제가 없을 수 있어요. 클래스·연간시간표·진도를 확인하거나 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'daily-records',
      mascot:
        '미니 메뉴에서 수업 프레젠테이션·수업기록·행동기록을 열 수 있어요. 수업 없는 칸은 수업 프레젠·수업기록이 비활성화돼요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="weekly-lesson-menu"], [data-tutorial-dash="daily-slot"], .cp-dashboard-slot--selectable',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'daily-todo',
      mascot:
        '오른쪽 TODO 노트에는 앞으로 약 2주 안의 미완료 할 일이 보여요. 카드를 길게 누르면 완료 처리할 수 있어요. 우측 위 복제·별 아이콘으로 복사하거나 즐겨찾기할 수 있어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="daily-todo"], .cp-dashboard-todo-block',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'daily-todo-add',
      mascot:
        'TODO 제목 옆 "+"로 새 할 일을 추가하고, 연필 아이콘으로 선택한 할 일을 수정할 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="daily-todo-actions"], .cp-dashboard-todo-head-actions',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'switch-weekly',
      mascot: '"위클리"를 눌러 주간 보기로 바꿔 주세요.',
      targetSelector: '[data-tutorial-dash="view-weekly"]',
      advanceWhenSelectorExists: '[data-tutorial-dash="weekly-wrap"], .cp-dash-weekly-wrap',
      section: '[대시보드] 위클리',
      sectionMascot: '한 주 수업·TODO를 한눈에 보는 구간이에요.',
    },
    {
      id: 'weekly-overview',
      mascot:
        '위클리에서는 요일마다 수업 블럭(왼쪽)과 그날 TODO(오른쪽)가 나란히 보여요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-dash="weekly-wrap"], .cp-dash-weekly-wrap',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'weekly-toolbar',
      mascot:
        '날짜 블럭 위 헤드에서 학년도·학기를 보고, 가운데 "<"·주 번호·">"로 주를 이동할 수 있어요. 주 번호를 누르면 이번 주로 돌아갑니다. 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="weekly-toolbar"], .cp-dash-planner-section-head',
      manualAdvance: true,
    },
    {
      id: 'open-calendar',
      mascot: '상단 가운데 달력 버튼을 눌러 연간 달력을 열어 주세요.',
      targetSelector:
        '[data-tutorial-dash="open-calendar"], .cp-dash-weekly-cal-btn-wrap .cp-apn-header-icon-btn',
      advanceWhenSelectorExists: '[data-tutorial-dash="year-cal-modal"], .cp-dash-year-cal-modal',
    },
    {
      id: 'calendar-modal',
      mascot:
        '학년도 기준으로 월별 달력과 학사 휴일을 한눈에 볼 수 있어요. 확인했으면 창을 닫거나 "다음"을 눌러 주세요. ("다음"을 누르면 달력도 함께 닫혀요.)',
      targetSelector: '[data-tutorial-dash="year-cal-modal"], .cp-dash-year-cal-modal',
      advanceWhenSelectorGone: '[data-tutorial-dash="year-cal-modal"], .cp-dash-year-cal-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
      clickOnManualAdvanceSelectors:
        '[data-tutorial-dash="year-cal-close"], .cp-dash-year-cal-modal-close, [aria-label="달력 닫기"]',
    },
    {
      id: 'weekly-lessons',
      mascot:
        '요일 칸의 수업 블럭을 누르면 메뉴가 열려요. 한 번 눌러 본 뒤 "다음"을 눌러 주세요. 수업이 없으면 "다음"으로 넘어가도 돼요.',
      targetSelector:
        '[data-tutorial-dash="weekly-slot"], .cp-dash-weekly-mini-slot.is-interactive',
      manualAdvance: true,
      targetMissingHint:
        '이 주에 표시할 수업이 없을 수 있어요. "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'weekly-lesson-menu',
      mascot:
        '메뉴에서 수업 프레젠테이션·수업기록·행동기록을 바로 열 수 있어요. 지금은 살펴만 보고 메뉴를 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-dash="weekly-lesson-menu"], .cp-dash-weekly-lesson-menu',
      advanceWhenSelectorGone:
        '[data-tutorial-dash="weekly-lesson-menu"], .cp-dash-weekly-lesson-menu',
      manualAdvance: true,
      blockTargetClick: true,
      allowManualAdvanceActions: true,
      targetMissingHint:
        '수업 블럭을 눌러 메뉴를 연 뒤 다시 보거나, "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'weekly-todos',
      mascot:
        '각 요일 오른쪽에는 그날 마감 TODO가 보여요. "+"로 그 날짜 할 일을 추가할 수 있어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '[data-tutorial-dash="weekly-todos"], .cp-dash-weekly-day-todos',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'outro',
      mascot:
        '대시보드 튜토리얼을 마쳤어요! 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const dashboardNote: TutorialDefinition = {
  ...dashboardNoteRaw,
  steps: dashboardNoteRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint: buildTutorialTargetMissingHint(recover, step.targetMissingHint),
    };
  }),
};

export default dashboardNote;
